import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle,
  Mail,
  Plus,
  RefreshCw,
  Save,
  X,
} from "lucide-react";
import ReactSelect from "react-select";
import { getDeliveryConfig, updateDeliveryConfig } from "../../api/settings";
import { getLogisticTeam } from "../../api/logisticTeam";
import { showToast } from "../../utils/showToast";
import { apiErrorMessage, apiErrorStatus } from "../../utils/apiErrorMessage";
import { cn } from "../../utils/cn";
import { SectionCard } from "../ui/SectionCard";
import { Switch } from "../ui/Switch";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { Skeleton } from "../ui/Skeleton";

// ─────────────────────────────────────────────────────────────────────────────
// RemitoRecipientsConfig — A quién se le envía por correo el remito (comprobante
// de colecta) cuando el repartidor cierra un retiro en ruta o se registra una
// recepción en bodega.
//
// Sección `remitoRecipients` del artefacto DeliveryConfig (GET/PATCH
// /configuration/delivery). Autocontenida igual que PickupFormConfig: el backend
// resuelve el operador dueño desde el JWT y mergea POR SECCIÓN, así que guardar
// acá no toca el formulario de cierre de colecta ni el resto de la config.
//
// Los defaults replican el comportamiento histórico (correo a los usuarios del
// comercio, nada más) y los aplica también microservice-reports cuando la
// sección no existe: deben calzar con los de allá.
// ─────────────────────────────────────────────────────────────────────────────

// A diferencia del resto del panel (UsersModule/TeamManagement), esta validación
// replica las reglas de `isEmail` de validator (la librería detrás del
// `@IsEmail` del DTO del BFF, update-delivery-config.dto.ts, con sus opciones
// por defecto): el regex del panel es más permisivo y dejaba pasar correos que
// el backend rechaza con un 400 que no dice cuál es el correo malo.
//   - parte local: solo los caracteres que acepta validator (letras, dígitos,
//     !#$%&'*+-/=?^_`{|}~ y unicode), sin punto inicial/final ni "..", hasta 64
//     bytes. Una coma, ";", ":", paréntesis, comillas, "<" o "[" la invalidan.
//     No se aceptan partes locales entre comillas (validator sí): se asume que
//     nadie configura un correo así para recibir remitos.
//   - dominio: etiquetas de letras/dígitos/guion/unicode de hasta 63 caracteres,
//     sin guion al inicio ni al final, separadas por un solo punto, con TLD de
//     2+ letras o "xn…" (punycode).
//   - total: hasta 254 caracteres.
const EMAIL_LOCAL_REGEX =
  /^[a-z\d!#$%&'*+\-/=?^_`{|}~\u00A1-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]+(?:\.[a-z\d!#$%&'*+\-/=?^_`{|}~\u00A1-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]+)*$/i;
const EMAIL_DOMAIN_LABEL_REGEX = /^[a-z\u00a1-\uffff0-9](?:[a-z\u00a1-\uffff0-9-]*[a-z\u00a1-\uffff0-9])?$/i;
const EMAIL_TLD_REGEX = /^(?:[a-z\u00A1-\u00A8\u00AA-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]{2,}|xn[a-z0-9-]{2,})$/i;
// Formas de ancho completo (ｅｘａｍｐｌｅ): validator las rechaza en el dominio.
const FULLWIDTH_REGEX = /[\uff01-\uff5e]/;

/** Largo en bytes UTF-8 (validator mide la parte local en bytes, no en caracteres). */
function utf8ByteLength(str) {
  return new Blob([str]).size;
}

function isValidEmail(email) {
  if (email.length > 254) return false;
  const at = email.lastIndexOf("@");
  if (at <= 0 || email.indexOf("@") !== at) return false;
  const local = email.slice(0, at);
  const domain = email.slice(at + 1);
  if (utf8ByteLength(local) > 64 || !EMAIL_LOCAL_REGEX.test(local)) return false;
  const labels = domain.split(".");
  if (labels.length < 2) return false;
  if (!EMAIL_TLD_REGEX.test(labels[labels.length - 1])) return false;
  return labels.every(
    (label) =>
      label.length <= 63 &&
      EMAIL_DOMAIN_LABEL_REGEX.test(label) &&
      !FULLWIDTH_REGEX.test(label),
  );
}

// Tope defensivo: sin límite, la lista libre termina siendo una lista de difusión.
const MAX_ADDITIONAL_EMAILS = 20;

/**
 * Valida y normaliza un correo escrito en el input contra la lista ya cargada.
 * Devuelve el correo listo para agregar, o un código de error.
 *
 * Se usa desde "Agregar" y TAMBIÉN al guardar: un correo tecleado y no agregado
 * viajaba fuera del PATCH y el toast confirmaba éxito igual, así que el operador
 * creía haberlo configurado.
 */
function validateNewEmail(raw, existing) {
  const email = String(raw ?? "").trim().toLowerCase();
  if (!email) return { email: "", error: "" };
  if (!isValidEmail(email)) return { email: "", error: "INVALID" };
  if (existing.includes(email)) return { email: "", error: "DUPLICATE" };
  if (existing.length >= MAX_ADDITIONAL_EMAILS) return { email: "", error: "LIMIT" };
  return { email, error: "" };
}

/**
 * Texto a mostrar cuando falla el PATCH de la sección.
 *
 * El BFF (bff-auth-gateway) responde 403 con un `message` propio
 * (configuration.controller.ts) y 400 con los mensajes de class-validator
 * unidos en UN solo string separados por "; " (main.ts, ValidationPipe), así
 * que la detección por campo se hace con `includes` sobre ese texto, nunca
 * asumiendo un array. Los 5xx y los fallos de red no exponen texto interno.
 */
function saveErrorMessage(err, t) {
  const fallback = t("No se pudo guardar la configuración");
  const status = apiErrorStatus(err);
  if (status === 403) {
    return apiErrorMessage(
      err,
      t("No tienes permiso para modificar la configuración de entrega."),
    );
  }
  if (status === 400) {
    const backend = apiErrorMessage(err, null);
    if (!backend) return fallback;
    if (backend.includes("additionalEmails") && backend.includes("must be an email")) {
      return t("Uno de los correos adicionales no es válido. Revísalo e inténtalo de nuevo.");
    }
    if (backend.includes("additionalEmails") && backend.includes("no more than")) {
      return t("Máximo {{n}} correos adicionales", { n: MAX_ADDITIONAL_EMAILS });
    }
    return backend;
  }
  // null = sin respuesta (red). El texto de un 5xx es ruido de infraestructura.
  if (status === null || status >= 500) return fallback;
  return apiErrorMessage(err, fallback);
}

/** Estado editable desde la respuesta del backend (o los defaults del contrato). */
function stateFromConfig(config) {
  const recipients = config?.remitoRecipients ?? null;
  return {
    // Default false: hasta ahora quien registró la colecta nunca recibió el remito.
    sendToCollector: recipients?.sendToCollector === true,
    // Default true: los usuarios del comercio son el destinatario histórico.
    sendToCommerceUsers: recipients?.sendToCommerceUsers !== false,
    operatorUserIds: Array.isArray(recipients?.operatorUserIds)
      ? [...recipients.operatorUserIds]
      : [],
    additionalEmails: Array.isArray(recipients?.additionalEmails)
      ? [...recipients.additionalEmails]
      : [],
  };
}

/** "Nombre Apellido — correo" para el selector; cae al correo o al id si falta algo. */
function teamUserLabel(user) {
  const fullName = `${user?.name ?? ""} ${user?.lastName ?? ""}`.trim();
  return [fullName, user?.email].filter(Boolean).join(" — ") || user?._id || "";
}

// react-select tematizado con los tokens del design system. Espejo del patrón
// TokenSelect de WhatsappAlertsPanel (el otro isMulti del proyecto).
const selectClassNames = {
  control: ({ isFocused }) =>
    cn(
      "flex items-center min-h-[40px] rounded-lg border bg-bg text-sm transition-colors",
      isFocused ? "border-transparent ring-2 ring-brand-500" : "border-border",
    ),
  valueContainer: () => "flex flex-wrap items-center gap-0.5 pl-2 pr-1 grow",
  placeholder: () => "text-fg-muted pl-1",
  singleValue: () => "text-fg pl-1",
  multiValue: () =>
    "flex items-center gap-1 rounded-md bg-brand-50 pl-2 pr-1 my-0.5 text-brand-700",
  multiValueLabel: () => "text-xs text-brand-700 py-0.5",
  multiValueRemove: () =>
    "cursor-pointer rounded hover:bg-brand-100 [&>svg]:w-3.5 [&>svg]:h-3.5",
  input: () => "text-fg [&_input]:focus:ring-0 [&_input]:shadow-none",
  indicatorsContainer: () => "flex items-center text-fg-muted",
  dropdownIndicator: () => "px-1.5 [&>svg]:w-4 [&>svg]:h-4",
  clearIndicator: () => "px-1 cursor-pointer hover:text-fg [&>svg]:w-4 [&>svg]:h-4",
  indicatorSeparator: () => "hidden",
  menu: () =>
    "mt-1 rounded-lg border border-border bg-bg shadow-elevated overflow-hidden z-[10000]",
  menuList: () => "py-1 max-h-60 overflow-y-auto",
  option: ({ isFocused, isSelected }) =>
    cn(
      "px-3 py-2 text-sm cursor-pointer",
      isSelected ? "bg-brand-500 text-white" : isFocused ? "bg-brand-50 text-brand-700" : "text-fg",
    ),
  noOptionsMessage: () => "px-3 py-2 text-sm text-fg-muted",
};
const selectStyles = { menuPortal: (base) => ({ ...base, zIndex: 10000 }) };

function TokenSelect(props) {
  return (
    <ReactSelect
      unstyled
      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
      menuPosition="fixed"
      menuShouldScrollIntoView={false}
      classNames={selectClassNames}
      styles={selectStyles}
      {...props}
    />
  );
}

export default function RemitoRecipientsConfig() {
  const { t } = useTranslation();

  const [state, setState] = useState(() => stateFromConfig(null));
  const [snapshot, setSnapshot] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  // Equipo interno del operador (para el selector). `null` = sin respuesta o con
  // error: se distingue de [] (equipo vacío de verdad) para no ocultar el fallo.
  const [team, setTeam] = useState(null);
  const [teamLoading, setTeamLoading] = useState(true);
  const [teamError, setTeamError] = useState(false);
  const [teamReloadKey, setTeamReloadKey] = useState(0);

  const [emailInput, setEmailInput] = useState("");
  const [emailError, setEmailError] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError(false);
    getDeliveryConfig()
      .then((res) => {
        if (!active) return;
        const next = stateFromConfig(res?.data ?? res);
        setState(next);
        setSnapshot(JSON.stringify(next));
      })
      .catch((err) => {
        if (!active) return;
        if (err?.response?.status === 403) {
          setForbidden(true);
        } else {
          // No se renderiza el formulario con defaults: con snapshot vacío, Guardar
          // quedaría habilitado y PATCHearía encima de la config real del operador.
          setLoadError(true);
          showToast(t("No se pudo cargar la configuración del remito"), false);
        }
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
    // `t` fuera de las deps a propósito: su identidad cambia con el idioma y eso
    // refetchearía la config, descartando ediciones sin aviso.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  useEffect(() => {
    let active = true;
    setTeamLoading(true);
    setTeamError(false);
    // getLogisticTeam nunca lanza: devuelve { data, error }.
    getLogisticTeam().then(({ data, error }) => {
      if (!active) return;
      if (error || !Array.isArray(data)) {
        setTeamError(true);
      } else {
        setTeam(data);
      }
      setTeamLoading(false);
    });
    return () => {
      active = false;
    };
  }, [teamReloadKey]);

  // El texto pendiente en el input cuenta como cambio: si no, con solo escribir
  // un correo el botón Guardar quedaba deshabilitado y no había forma de
  // incorporarlo salvo pulsar "Agregar" (handleSave ya lo contempla).
  const dirty = useMemo(
    () => JSON.stringify(state) !== snapshot || emailInput.trim() !== "",
    [state, snapshot, emailInput],
  );

  // Solo usuarios activos como opciones nuevas: microservice-reports filtra los
  // inactivos al enviar, así que ofrecerlos sería configurar un destinatario que
  // nunca recibe nada.
  const teamOptions = useMemo(
    () =>
      (team ?? [])
        .filter((u) => u.active !== false)
        .map((u) => ({ value: u._id, label: teamUserLabel(u) })),
    [team],
  );

  // Los ids ya guardados se muestran SIEMPRE (aunque el usuario esté desactivado o
  // ya no aparezca en el listado): si no, el chip desaparecería de la vista pero
  // seguiría viajando en el PATCH, imposible de quitar desde la UI.
  const selectedTeamOptions = useMemo(
    () =>
      state.operatorUserIds.map((id) => {
        const user = (team ?? []).find((u) => u._id === id);
        if (!user) return { value: id, label: t("Usuario no disponible") };
        return {
          value: id,
          label:
            user.active === false
              ? `${teamUserLabel(user)} (${t("desactivado")})`
              : teamUserLabel(user),
        };
      }),
    [state.operatorUserIds, team, t],
  );

  const atEmailLimit = state.additionalEmails.length >= MAX_ADDITIONAL_EMAILS;

  const emailErrorMessage = (code) => {
    if (code === "INVALID") return t("Ingresa un correo válido, por ejemplo nombre@dominio.com");
    if (code === "DUPLICATE") return t("Ese correo ya está en la lista");
    if (code === "LIMIT") return t("Máximo {{n}} correos adicionales", { n: MAX_ADDITIONAL_EMAILS });
    return "";
  };

  const handleAddEmail = () => {
    // El correo se normaliza a minúsculas: el backend deduplica case-insensitive
    // al enviar, pero guardarlo normalizado evita duplicados "visuales" acá.
    const { email, error } = validateNewEmail(emailInput, state.additionalEmails);
    if (!email) {
      if (error) setEmailError(emailErrorMessage(error));
      return;
    }
    setState((prev) => ({
      ...prev,
      additionalEmails: [...prev.additionalEmails, email],
    }));
    setEmailInput("");
    setEmailError("");
  };

  const handleRemoveEmail = (email) =>
    setState((prev) => ({
      ...prev,
      additionalEmails: prev.additionalEmails.filter((e) => e !== email),
    }));

  // Aviso (no bloqueo): el contrato permite guardar sin destinatarios — el remito
  // se genera igual y simplemente no se envía por correo.
  const noRecipients =
    !state.sendToCollector &&
    !state.sendToCommerceUsers &&
    state.operatorUserIds.length === 0 &&
    state.additionalEmails.length === 0;

  const handleSave = async () => {
    // Un correo escrito en el input pero no agregado con "Agregar" se incorpora
    // acá en vez de perderse; si no es válido, se bloquea el guardado con el
    // mismo mensaje del formulario en vez de descartarlo en silencio.
    const pending = validateNewEmail(emailInput, state.additionalEmails);
    if (emailInput.trim() && !pending.email) {
      setEmailError(emailErrorMessage(pending.error));
      return;
    }
    const nextState = pending.email
      ? { ...state, additionalEmails: [...state.additionalEmails, pending.email] }
      : state;

    setSaving(true);
    try {
      // La sección viaja SIEMPRE completa: el backend mergea por sección, no por
      // campo, y un parcial borraría lo que no viajó.
      await updateDeliveryConfig({
        remitoRecipients: {
          sendToCollector: nextState.sendToCollector,
          sendToCommerceUsers: nextState.sendToCommerceUsers,
          operatorUserIds: nextState.operatorUserIds,
          additionalEmails: nextState.additionalEmails,
        },
      });
      setState(nextState);
      setSnapshot(JSON.stringify(nextState));
      setEmailInput("");
      setEmailError("");
      showToast(t("Destinatarios del remito guardados"), true);
    } catch (err) {
      // El mensaje del backend explica el rechazo (permisos, formato); antes se
      // descartaba y el usuario solo veía un toast fijo sin saber qué corregir.
      showToast(saveErrorMessage(err, t), false);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (snapshot) setState(JSON.parse(snapshot));
    setEmailInput("");
    setEmailError("");
  };

  if (loading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (forbidden) {
    return (
      <SectionCard
        icon={<AlertTriangle className="w-4 h-4" />}
        title={t("Sin configuración disponible")}
        subtitle={t(
          "Tu usuario no pertenece a un operador con configuración de entrega.",
        )}
      />
    );
  }

  if (loadError) {
    return (
      <SectionCard
        icon={<AlertTriangle className="w-4 h-4" />}
        title={t("No se pudo cargar la configuración")}
        subtitle={t("Reintenta en un momento.")}
        action={
          <Button
            variant="outline"
            onClick={() => setReloadKey((k) => k + 1)}
            iconLeft={<RefreshCw size={16} />}
          >
            {t("Reintentar")}
          </Button>
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <SectionCard
        icon={<Mail className="w-4 h-4" />}
        title={t("Envío del remito")}
        subtitle={t(
          "A quién le llega por correo el remito al cerrar un retiro en ruta o una recepción en bodega.",
        )}
      >
        <div className="flex flex-col gap-5">
          <div>
            <Switch
              checked={state.sendToCollector}
              onChange={(next) => setState((prev) => ({ ...prev, sendToCollector: next }))}
              disabled={saving}
              label={t("Al repartidor que retiró")}
            />
            <p className="mt-1.5 text-xs text-fg-subtle">
              {t(
                "En una recepción en bodega se envía a quien registró la recepción.",
              )}
            </p>
          </div>

          <div>
            <Switch
              checked={state.sendToCommerceUsers}
              onChange={(next) =>
                setState((prev) => ({ ...prev, sendToCommerceUsers: next }))
              }
              disabled={saving}
              label={t("A los usuarios del comercio")}
            />
            <p className="mt-1.5 text-xs text-fg-subtle">
              {t("Los usuarios vigentes del comercio dueño de los paquetes.")}
            </p>
          </div>

          <div>
            <label
              htmlFor="remito-operator-users"
              className="block text-sm font-medium text-fg mb-1.5"
            >
              {t("Usuarios del operador")}
            </label>
            <TokenSelect
              isMulti
              inputId="remito-operator-users"
              placeholder={t("Elige usuarios de tu equipo")}
              noOptionsMessage={() => t("No hay usuarios disponibles")}
              isLoading={teamLoading}
              isDisabled={saving || teamError}
              options={teamOptions}
              value={selectedTeamOptions}
              onChange={(vals) =>
                setState((prev) => ({
                  ...prev,
                  operatorUserIds: (vals || []).map((v) => v.value),
                }))
              }
            />
            {teamError ? (
              <div className="mt-1.5 flex items-center gap-2">
                <p className="text-xs text-status-error">
                  {t("No pudimos cargar el equipo de tu empresa.")}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setTeamReloadKey((k) => k + 1)}
                  iconLeft={<RefreshCw size={14} />}
                >
                  {t("Reintentar")}
                </Button>
              </div>
            ) : (
              <p className="mt-1.5 text-xs text-fg-subtle">
                {t("Miembros del equipo interno de tu empresa que también recibirán el remito.")}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="remito-additional-email"
              className="block text-sm font-medium text-fg mb-1.5"
            >
              {t("Correos adicionales")}
            </label>
            <div className="flex items-center gap-2">
              <Input
                id="remito-additional-email"
                type="email"
                placeholder={t("correo@ejemplo.com")}
                value={emailInput}
                invalid={!!emailError}
                disabled={saving || atEmailLimit}
                onChange={(e) => {
                  setEmailInput(e.target.value);
                  if (emailError) setEmailError("");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    // Sin esto, Enter dispararía el submit de cualquier form ancestro.
                    e.preventDefault();
                    handleAddEmail();
                  }
                }}
              />
              <Button
                variant="outline"
                onClick={handleAddEmail}
                disabled={saving || atEmailLimit || !emailInput.trim()}
                iconLeft={<Plus size={16} />}
              >
                {t("Agregar")}
              </Button>
            </div>
            <div aria-live="polite">
              {emailError && (
                <p className="mt-1.5 text-xs text-status-error">{emailError}</p>
              )}
            </div>
            {state.additionalEmails.length > 0 && (
              <ul className="mt-2 flex flex-wrap gap-1.5">
                {state.additionalEmails.map((email) => (
                  <li
                    key={email}
                    className="flex items-center gap-1 rounded-md bg-brand-50 pl-2 pr-1 py-0.5 text-xs text-brand-700"
                  >
                    {email}
                    <button
                      type="button"
                      aria-label={t("Quitar {{email}}", { email })}
                      onClick={() => handleRemoveEmail(email)}
                      disabled={saving}
                      className="rounded p-0.5 hover:bg-brand-100 disabled:opacity-50"
                    >
                      <X className="w-3.5 h-3.5" aria-hidden="true" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <p className="mt-1.5 text-xs text-fg-subtle">
              {atEmailLimit
                ? t("Alcanzaste el máximo de {{n}} correos adicionales.", {
                    n: MAX_ADDITIONAL_EMAILS,
                  })
                : t("Correos que recibirán el remito aunque no tengan cuenta. Máximo {{n}}.", {
                    n: MAX_ADDITIONAL_EMAILS,
                  })}
            </p>
          </div>

          <div aria-live="polite">
            {noRecipients && (
              <p className="flex items-start gap-1.5 text-xs text-status-warning">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" aria-hidden="true" />
                {t(
                  "Con esta configuración el remito no se enviará a nadie. Se seguirá generando, pero sin destinatarios de correo.",
                )}
              </p>
            )}
          </div>
        </div>
      </SectionCard>

      <div className="flex items-center justify-end gap-2 border-t border-border pt-4">
        <Button variant="outline" onClick={handleReset} disabled={!dirty || saving}>
          {t("Descartar cambios")}
        </Button>
        <Button
          onClick={handleSave}
          loading={saving}
          disabled={!dirty || saving}
          iconLeft={!saving ? <Save size={16} /> : undefined}
        >
          {saving ? t("Guardando…") : t("Guardar cambios")}
        </Button>
      </div>
    </div>
  );
}
